import { lumi } from '@/lib/lumi'

export type VerifyLaunchKind = 'curve' | 'presale'

export type VerifyLaunchInput = {
  kind: VerifyLaunchKind
  address: string
  token?: string
}

const verifiedKeys = new Set<string>()
const inFlight = new Set<string>()
const lastAttempt = new Map<string, number>()
const BACKOFF_MS = 20 * 1000

function queueKey(input: VerifyLaunchInput) {
  return `${input.kind}:${(input.address || '').trim().toLowerCase()}:${(input.token || '').trim().toLowerCase()}`
}

function delay(ms: number) {
  return new Promise(resolve => setTimeout(resolve, ms))
}

function isVerifiedResult(payload: unknown) {
  if (!payload || typeof payload !== 'object')
    return false
  const record = payload as {
    verified?: boolean
    submitted?: boolean
    contracts?: Array<{ verified?: boolean, submitted?: boolean }>
  }
  if (record.verified === true)
    return true
  if (Array.isArray(record.contracts) && record.contracts.length > 0 && record.contracts.every(item => item?.verified === true))
    return true
  return false
}

/** Fire-and-forget Blockscout verification. Never throw into createLaunch/createPresale. */
export function queueLaunchVerification(input: VerifyLaunchInput) {
  const address = (input.address || '').trim()
  if (!address)
    return
  const key = queueKey(input)
  if (verifiedKeys.has(key) || inFlight.has(key))
    return
  const previous = lastAttempt.get(key) ?? 0
  if (previous && Date.now() - previous < BACKOFF_MS)
    return
  inFlight.add(key)
  lastAttempt.set(key, Date.now())
  void requestLaunchVerification(input)
    .then((ok) => {
      if (ok)
        verifiedKeys.add(key)
    })
    .finally(() => {
      inFlight.delete(key)
    })
}

export async function requestLaunchVerification(input: VerifyLaunchInput) {
  const address = (input.address || '').trim()
  if (!address)
    return false
  const delays = [0, 12_000]
  for (const wait of delays) {
    if (wait)
      await delay(wait)
    try {
      const result = await lumi.functions.invoke('verifyLaunch', {
        method: 'POST',
        body: {
          kind: input.kind,
          address,
          token: input.token,
        },
      })
      if (isVerifiedResult(result))
        return true
    }
    catch (error: unknown) {
      const record = error as { data?: { error?: string } }
      const message = (record?.data?.error || 'Blockscout verification is temporarily unavailable').slice(0, 180)
      console.warn(`[verifyLaunch] ${address}: ${message}`)
    }
  }
  return false
}
